/**
 * Draft Save Dialog
 *
 * Confirmation dialog before starting new chat or closing with unsent draft.
 * Prevents accidental loss of work.
 */

import { MessageSquare, Save, Trash2 } from 'lucide-react';
import React from 'react';
import type { DraftAttachment } from '@ctrl-enter/types';
import { cn } from '../utils/cn';
import { Button } from './button';
import { Dialog } from './dialog';
import { IconButton } from './icon-button';

export type DraftSaveAction = 'save' | 'discard' | 'cancel';

export type DraftSaveDialogProps = {
  isOpen: boolean;
  draftMessage: string;
  draftAttachments: DraftAttachment[];
  actionType: 'new-chat' | 'close';
  onAction: (action: DraftSaveAction) => void;
  className?: string;
};

export const DraftSaveDialog: React.FC<DraftSaveDialogProps> = ({
  isOpen,
  draftMessage,
  draftAttachments,
  actionType,
  onAction,
  className,
}) => {
  const hasMessage = draftMessage.trim().length > 0;
  const hasAttachments = draftAttachments.length > 0;
  const charCount = draftMessage.trim().length;

  const getTitle = () => {
    if (actionType === 'new-chat') {
      return 'Save Your Draft?';
    }
    return 'Save Before Closing?';
  };

  const getDescription = () => {
    if (actionType === 'new-chat') {
      return 'You have an unsent message. Would you like to save it before starting a new chat?';
    }
    return 'You have an unsent message. Would you like to save it before closing?';
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => onAction('cancel')}
      size="md"
      className={cn('flex flex-col', className)}
    >
        {/* Dialog body */}
        <div className="rounded-xl w-full">
          {/* Header */}
          <div className="flex items-start justify-between p-6 pb-4 border-b rounded-xl glass-surface-static-bg glass-surface-border m-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">{getTitle()}</h2>
              </div>
            </div>
            <IconButton
              iconName="X"
              variant="ghost"
              size="sm"
              onClick={() => onAction('cancel')}
              aria-label="Close dialog"
            />
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">{getDescription()}</p>

            {/* Draft preview */}
            <div className="glass-surface rounded-lg p-3 space-y-2">
              {hasMessage && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-foreground">Message draft:</p>
                  <p className="text-xs text-muted-foreground line-clamp-3">{draftMessage}</p>
                  <p className="text-xs text-muted-foreground">
                    {charCount} character{charCount !== 1 ? 's' : ''}
                  </p>
                </div>
              )}

              {hasAttachments && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-foreground">
                    {hasMessage && 'With'} {draftAttachments.length} attachment
                    {draftAttachments.length !== 1 ? 's' : ''}:
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-0.5">
                    {draftAttachments.slice(0, 3).map(attachment => (
                      <li key={attachment.id} className="truncate">
                        • {attachment.name}
                      </li>
                    ))}
                    {draftAttachments.length > 3 && (
                      <li className="text-xs text-muted-foreground">
                        and {draftAttachments.length - 3} more...
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            {/* Info message */}
            <div className="glass-surface rounded-lg p-3">
              <p className="text-xs text-muted-foreground leading-relaxed">
                <strong>Tip:</strong>{' '}
                {actionType === 'new-chat'
                  ? 'Your draft will be automatically restored in the new chat.'
                  : 'Saved drafts are stored locally and will be available when you return.'}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className=" p-6 space-y-2">
            <Button variant="primary" onClick={() => onAction('save')} className="w-full gap-2">
              <Save className="w-4 h-4" />
              Save & {actionType === 'new-chat' ? 'Start New' : 'Close'}
            </Button>
            <Button
              variant="destructive"
              onClick={() => onAction('discard')}
              className="w-full gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Discard & {actionType === 'new-chat' ? 'Start New' : 'Close'}
            </Button>
            <Button variant="secondary" onClick={() => onAction('cancel')} className="w-full">
              Cancel
            </Button>
          </div>
        </div>
    </Dialog>
  );
};

DraftSaveDialog.displayName = 'DraftSaveDialog';
